import { useEffect, useState } from 'react';
import uniq from 'lodash/uniq';
import Button from 'epn-ui/umd/dist/components/Button';
import InputText from 'epn-ui/umd/dist/components/InputText';
import Form from 'epn-ui/umd/dist/components/Form';

import api from '@api';
import BaseSemanticLink from '@base/BaseSemanticLink';
import useI18n from '@i18n';
import routes from '@config/routes.json';
import { aliexpressRegexp } from '@components/pages/creatives/config';
import sendGaEvent from 'src/utils/metrics/ga';
import { formatResponse } from '../utils';
import { IGroupedLinks, IRequest } from '../index.d';

interface IForm {
    setSubmitError: (value: boolean) => void;
    setResult: (data: IGroupedLinks) => void;
}

const LinkCheckerForm: React.FC<IForm> = ({ setSubmitError, setResult }) => {
    const [isPending, setIsPending] = useState(false);
    const [error, setError] = useState(false);
    const [isNotAliLink, setIsNotAliLink] = useState(false);
    const { t } = useI18n('LinkChecker');
    const [form] = Form.useForm();
    const links = Form.useWatch('links', form);

    const checkLinks = async ({ links: currentLinks }) => {
        try {
            setIsPending(true);
            const notEmptyStrings = currentLinks?.split('\n')?.filter(link => !!link.trim());
            const requests: IRequest[] = uniq(notEmptyStrings).map((link, index) => ({
                type: 'request',
                id: index + 1,
                attributes: {
                    url: `/affiliate/checkLink?v=2.2&link=${link}`,
                    method: 'GET',
                    body: [],
                },
            }));

            sendGaEvent({
                eventCategory: 'link_checker',
                eventAction: 'check',
                eventLabel: 'link_number',
                eventValue: requests.length,
            });

            const res = await api.batch.batch({ body: { data: requests } });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const data = formatResponse(res as any, requests);
            setResult(data);
            setIsPending(false);
        } catch (errors) {
            console.error(errors);
            setSubmitError(true);
        }
    };

    const splitValue = (value: string): string[] => {
        return value
            .split('\n')
            .map(link => link.trim())
            .filter(link => !!link);
    };

    const onChangeLinks = (currentLinks: string) => {
        // const { value } = e.target;
        const parsedValue = splitValue(currentLinks);

        // Условие: Не больше 10 ссылок
        if (parsedValue.length > 10) {
            form.setFields([
                {
                    name: 'links',
                    value: currentLinks,
                    errors: [t('Maximum 10 links')],
                },
            ]);
            setError(true);
            return;
        }

        // Условие: Минимум 1 ссылка
        if (parsedValue.length === 0) {
            form.setFields([
                {
                    name: 'links',
                    value: currentLinks,
                    errors: [t('Please enter at least one link')],
                },
            ]);
            setError(true);
            return;
        }

        // Условие: Все ссылки должны соответствовать aliexpressRegexp
        const invalidLink = parsedValue.find(link => !link.match(aliexpressRegexp));
        if (invalidLink) {
            form.setFields([
                {
                    name: 'links',
                    value: currentLinks,
                    errors: [`${t('Incorrect link:')} ${invalidLink}`],
                },
            ]);
            setError(true);
            return;
        }

        // Если всё в порядке, очищаем ошибки и обновляем `setValue`
        form.setFields([{ name: 'links', value: currentLinks, errors: [] }]);
        setError(false);
    };

    const showTelegramBot = process.env.NEXT_PUBLIC_PROJECT_NAME === 'epn';

    useEffect(() => {
        if (links) {
            onChangeLinks(links);
            setIsNotAliLink(!splitValue(links).every(link => link.match(aliexpressRegexp)));
        } else {
            form.setFields([{ name: 'links', value: links, errors: [] }]);
            setIsNotAliLink(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [links]);

    return (
        <Form onFinish={checkLinks} form={form}>
            <Form.Item name="links" required>
                <InputText.TextArea
                    className="!min-h-14"
                    id="LinkCheckerPageLinksInput"
                    placeholder={t('Paste a list of links, where each link begins on a new line')}
                    autoSize={{ minRows: 2 }}
                />
            </Form.Item>

            {showTelegramBot && (
                <p className="mt-2 text-p3 text-blueGray-800">
                    {t('You can check up to 10 links at once. Place each link at a new line')}
                    <br />
                    {t('Need more?')}
                    <BaseSemanticLink to={routes.telegramBot} className="underline">
                        {t('Telegram-bot')}
                    </BaseSemanticLink>
                    {t('checks up to 50 links at once')}
                </p>
            )}

            {isNotAliLink && (
                <p className="mt-1 text-red-500 text-p3">
                    {t('One or more links are not AliExpress links')}
                </p>
            )}

            <div className="flex justify-end mt-[24px]">
                <Button
                    id="LinkCheckerCheckButton"
                    type="primary"
                    htmlType="submit"
                    loading={isPending}
                    disabled={error || !links}
                    className="w-full xs:w-auto"
                >
                    {t('Check')}
                </Button>
            </div>
        </Form>
    );
};

export default LinkCheckerForm;
